import { type ReactNode, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSimpleLoader } from "~/HousingSelectionAssistant/hooks/useSimpleLoader";
import Spinner from "~/common/components/Spinner/Spinner";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";

import classes from "./QuoteDetailsPage.module.less";
import type { QuoteDetailsMO } from "~/OrdersQuotes/common/components/v2/models";
import type { PriceData } from "~/models/PriceData";
import { sum } from "~/utils/PriceUtils";
import {
  getOrderLineItemSurchargeValue,
  getQuoteLineItemPriceVisibility,
  QuoteLineItemPriceVisibility,
} from "~/OrdersQuotes/common/utils/OrderLineItemUtils";
import { QuoteDetailsSummary } from "~/OrdersQuotes/QuoteDetailsPage/components/v2/QuoteDetailsSummary";
import QuoteSummaryTotals from "~/OrdersQuotes/QuoteDetailsPage/components/QuoteSummaryTotals";
import { Button } from "~/common/components/Button/Button";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { QuoteDetailsTable } from "~/OrdersQuotes/common/components/v2/QuoteTable/QuoteDetailsTable";
import { IncludesSurchargeDisclaimer } from "~/common/components/Messages/IncludesSurchargeDisclaimer";
import { Permission, userHasPermission } from "~/utils/permissions";
import { fetchQuoteDetails } from "~/OrdersQuotes/QuoteListPage/api/fetchOrderDetails/fetchQuoteDetails";
import { transformQuoteDetails } from "~/OrdersQuotes/QuoteListPage/api/fetchOrderDetails/v2/transformQuoteDetails";
import { AccountErrorMessage } from "~/OrdersQuotes/QuoteDetailsPage/AccountErrorMessage";
import type { AddToCartApiItem } from "~/Cart/api/AddToCart";
import { sendData } from "~/api/QuoteToCart";
import { useDispatch } from "react-redux";
import { addMessage } from "~/redux/globalMessages/actions";
import { useQuoteDetailsSelectionState } from "~/OrdersQuotes/common/components/v2/QuoteTable/useQuoteDetailsSelectionState";
import { makeMapQuoteLineToATCAPIItem } from "~/OrdersQuotes/QuoteListPage/utils";
import CartSuccessModal from "~/AddToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { openModal } from "~/AddToCart/ModalSevice";
import { addBaseProductDataAction } from "~/redux/products/actions";
import { DownloadContentButton } from "~/OrdersQuotes/QuoteListPage/components/QuoteListTable/DownloadContentButton";
import { downloadFile } from "~/api/ApiHelper";
import { Alert } from "~/common/m3/Alert/Alert";

export function createQuoteDetailsPage(data: { previousAccountUrl: string }) {
  const params = new URLSearchParams(window.location.search);
  const quoteNumber = params.get("id") || "";
  return (
    <>
      {data.previousAccountUrl && <AccountErrorMessage {...data} />}
      <QuoteDetailsPage quoteNumber={quoteNumber} />
    </>
  );
}

interface Props {
  quoteNumber: string;
}

export const QuoteDetailsPage = ({ quoteNumber }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: quote, state } = useSimpleLoader(async () => {
    const dto = await fetchQuoteDetails(quoteNumber);

    const data = transformQuoteDetails(dto);
    dispatch(addBaseProductDataAction(data.quoteLines.map((entry) => entry.product)));
    return data;
  }, [quoteNumber]);

  let content: ReactNode = null;
  if (!quoteNumber) {
    content = (
      <Alert className="mt-3" severity="error">
        {t("quoteDetailsPage.error.missingOrder")}
      </Alert>
    );
  } else if (state === "loading") {
    content = <Spinner className="mt-3" />;
  } else if (state === "error" || !quote) {
    content = (
      <Alert className="mt-3" severity="error">
        {t("quoteDetailsPage.error.general")}
      </Alert>
    );
  } else {
    content = <LoadedQuoteDetailsPage quote={quote} />;
  }
  return (
    <div className="scha__order-details-page">
      <PageTitle className={classes.pageTitle}>{t("quoteDetailsPage.title")}</PageTitle>
      {content}
    </div>
  );
};

const LoadedQuoteDetailsPage = ({ quote }: { quote: QuoteDetailsMO }) => {
  const { t } = useTranslation();

  const cboxProps = useQuoteDetailsSelectionState();
  useLayoutEffect(() => {
    cboxProps.init(quote);
  }, [quote]);

  const { addSelectedQuotesToCart, isSending } = useAddToCartLogic({
    quote,
    selectedLineNos: cboxProps.selectedLineNos,
  });

  const totalSurcharge = calculateTotalSurcharge(quote);
  const containsSurcharge = totalSurcharge.value > 0;

  const canPurchase = userHasPermission(Permission.PLACE_ORDER);

  const disableCheckoutBtn = quote.disabled || !cboxProps.isAnythingSelected;
  const showDisabledMsg = disableCheckoutBtn && cboxProps.isAnythingSelected;

  // In case we have more reasons to show checkboxes in the future
  const showCheckboxes = canPurchase;

  const actionButtons = (
    <>
      <Button
        data-test-id={"add-all-to-cart"}
        disabled={disableCheckoutBtn || isSending}
        onClick={addSelectedQuotesToCart}
      >
        {t("quoteDetailsPage.checkout")}
      </Button>
    </>
  );

  const disabledButtonMessage = showDisabledMsg && (
    <div className={classes.disabledAtcMessage}>
      <Alert severity="info">{t("quoteDetailsPage.summary.disabledBtnMsg")}</Alert>
    </div>
  );

  const totals = (
    <QuoteSummaryTotals
      totalSelectableWeight={quote.totalSelectableWeight}
      totalSelectableValue={quote.totalSelectablePrice}
      totalValue={quote.totalPrice}
      surcharge={containsSurcharge ? totalSurcharge : undefined}
    />
  );

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.summary}>
          <QuoteDetailsSummary quote={quote} />
        </div>
        <div className={classes.upperTotals}>{totals}</div>
      </div>
      <div className={classes.tableContainer}>
        <div className={classes.hideInPrint}>
          <div className={classes.tableTitle}>
            <h3 className="m-0">{t("quoteDetailsPage.table.title")}</h3>
          </div>
          <div className={classes.upperActionButtons}>{actionButtons}</div>
          {disabledButtonMessage}

          {quote.quoteDocumentUrl && (
            <DownloadInvoices
              className={classes.downloadPDF}
              quoteDocumentUrl={quote.quoteDocumentUrl}
              soNumber={quote.soNumber}
            />
          )}

          <QuoteDetailsTable
            quoteLines={quote.quoteLines}
            className={classes.table}
            showCheckboxes={showCheckboxes}
            {...cboxProps}
          />
        </div>
      </div>

      <div className={classes.footer}>
        <hr className={classes.footerSeparator} />
        <div className={classes.lowerTotals}>{totals}</div>
        <hr className={classes.totalsSeparator} />
        <div className={classes.lowerActionButtons}>{actionButtons}</div>
        {disabledButtonMessage}
      </div>
      {<IncludesSurchargeDisclaimer hasSurcharge={containsSurcharge} className={classes.surchargeDisclaimer} />}
    </div>
  );
};

function calculateTotalSurcharge(data: QuoteDetailsMO): PriceData {
  return sum(
    ...data.quoteLines
      .filter((lineItem) => {
        return (
          getQuoteLineItemPriceVisibility({
            lineItemStatus: lineItem.status,
            quoteStatus: data.status,
          }) === QuoteLineItemPriceVisibility.VISIBLE
        );
      })
      .flatMap((lineItem) => lineItem.parts)
      .map((part) => getOrderLineItemSurchargeValue(part)),
  );
}

const DownloadInvoices = ({
  quoteDocumentUrl,
  soNumber,
  className,
}: {
  quoteDocumentUrl?: string;
  soNumber: string;
  className?: string;
}) => {
  const { t } = useTranslation();
  const [loadingDownload, setLoadingDownload] = useState(false);

  const handleDownload = async () => {
    setLoadingDownload(true);
    if (quoteDocumentUrl && soNumber) {
      await downloadFile(`${quoteDocumentUrl}?id=${soNumber}`);
    }
    setLoadingDownload(false);
  };

  return (
    <Stacker direction={"row"} gap={16} className={className}>
      <span className="f-label-color">{t("quoteDetailsPage.downloadFile.title")}</span>
      <DownloadContentButton
        label={t("downloadFile.type.pdf")}
        onClick={handleDownload}
        isLoading={loadingDownload}
        labelHover={t("quoteListPage.downloadDetails.helperLabel")}
      />
    </Stacker>
  );
};

const useAddToCartLogic = ({
  quote,
  selectedLineNos,
}: {
  quote: QuoteDetailsMO;
  selectedLineNos: Record<string, boolean>;
}) => {
  const [isSending, setIsSending] = useState(false);
  const dispatch = useDispatch();

  const addSelectedQuotesToCart = async () => {
    // TODO: There is no logic for bundled quote & alternatives.
    // For now we assume that all need to be selected for bundle quote to work
    const selectedLineItems = quote.quoteLines.filter((line) => selectedLineNos[line.lineNo]);

    const isAllSelected = selectedLineItems.length === quote.quoteLines.length;
    const isAllDisabled = quote.quoteLines.every((line) => line.disabled);

    if (quote.isBundledQuote && !isAllSelected) {
      // TODO
      throw new Error("Bundled quote not fully selected");
    }

    if (selectedLineItems.flatMap((line) => line.parts).some((part) => !part.price)) {
      throw new Error("Some parts do not have a price");
    }

    if (quote.disabled || isAllDisabled) {
      return;
    }

    setIsSending(true);

    const atcItems: AddToCartApiItem[] = selectedLineItems.flatMap(makeMapQuoteLineToATCAPIItem());

    try {
      const response = await sendData({
        quoteNumber: quote.soNumber,
        items: atcItems,
      });
      if (response.ok) {
        openModal("add-to-cart-dialog", (close) => (
          <CartSuccessModal formData={atcItems} onClose={close} page="QUOTE_DETAILS" />
        ));
        //dispatch(addMessage("info", "page.quoteDetails.addToCart.success", "translation"));
      } else {
        dispatch(addMessage("error", "page.quoteDetails.errorSending", "translation"));
      }
    } catch (e) {
      console.error(e);
      dispatch(addMessage("error", "page.quoteDetails.errorSending", "translation"));
    } finally {
      setIsSending(false);
    }
  };

  return {
    isSending,
    addSelectedQuotesToCart,
  };
};
